import type { TaskDesignFreedom, TaskPlan } from "@calcplan/ai-capabilities";

export interface AgentQuestionPrompt {
  subject: string;
  reason: string;
  question: string;
  options: string[];
  allowFreeText: boolean;
  kind?: "catalog" | "general";
  catalogChoices?: CatalogChoice[];
}

export interface CatalogCandidate {
  catalogItemId: string | null;
  id: string | null;
  stableItemId: string | null;
  revision: string | null;
  name: string;
  type: string | null;
  category: string | null;
  description: string | null;
  dimensionsMm: { width: number; height: number; depth: number } | null;
  resize: unknown;
  placementHint: string | null;
  availability: string | null;
}

export interface CatalogChoice {
  label: string;
  candidate: CatalogCandidate;
}

export function catalogCandidatesFromResult(result: unknown): CatalogCandidate[] {
  if (!result || typeof result !== "object") return [];
  const items = (result as Record<string, unknown>).items;
  if (!Array.isArray(items)) return [];
  return items.flatMap((item): CatalogCandidate[] => {
    if (!item || typeof item !== "object") return [];
    const value = item as Record<string, unknown>;
    const name = typeof value.name === "string" ? value.name.trim() : "";
    if (!name) return [];
    const dimensions = value.dimensionsMm && typeof value.dimensionsMm === "object"
      ? value.dimensionsMm as Record<string, unknown>
      : null;
    const width = Number(dimensions?.width ?? 0);
    const height = Number(dimensions?.height ?? 0);
    const depth = Number(dimensions?.depth ?? 0);
    return [{
      catalogItemId: text(value.catalogItemId) ?? text(value.stableItemId) ?? text(value.id),
      id: text(value.id),
      stableItemId: text(value.stableItemId),
      revision: text(value.revision),
      name,
      type: typeof value.type === "string" ? value.type : null,
      category: typeof value.category === "string" && value.category.trim() ? value.category.trim() : null,
      description: typeof value.description === "string" && value.description.trim() ? value.description.trim() : null,
      dimensionsMm: [width, height, depth].every(Number.isFinite) && [width, height, depth].some((size) => size > 0)
        ? { width, height, depth }
        : null,
      resize: value.resize ?? null,
      placementHint: text(value.placementHint),
      availability: text(value.availability),
    }];
  }).slice(0, 8);
}

export function catalogGuidancePrompt(input: {
  query: string;
  requestedType: string | null;
  step: string;
  attemptedQueries: string[];
  candidates: CatalogCandidate[];
}): AgentQuestionPrompt {
  const requestedType = input.requestedType ?? input.candidates.find((candidate) => candidate.type)?.type ?? null;
  const material = requestedType === "material" || /(?:mdf|material|cor|acabamento)/iu.test(input.query);
  const target = clip(input.step || "esta etapa do projeto", 90);
  const query = clip(input.query || (material ? "o material solicitado" : "o módulo solicitado"), 70);
  const attempted = [...new Set(input.attemptedQueries.map((item) => item.trim()).filter(Boolean))]
    .slice(-3)
    .map((item) => `“${clip(item, 42)}”`)
    .join(", ");
  const candidates = input.candidates
    .filter((candidate) => !requestedType || !candidate.type || candidate.type === requestedType)
    .slice(0, 4);
  const catalogChoices = candidates.map((candidate) => ({ label: candidateLabel(candidate), candidate }));

  return {
    subject: material ? `Material para ${target}` : `Módulo para ${target}`,
    reason: `Não encontrei um resultado exato para “${query}”${attempted ? ` após buscar ${attempted}` : ""}. ${candidates.length ? "Encontrei estas alternativas próximas." : "Preciso da sua referência para não escolher o item errado."}`,
    question: material
      ? `Qual acabamento devo aplicar em ${target}?`
      : `Qual módulo devo usar em ${target}?`,
    options: catalogChoices.map((choice) => choice.label),
    allowFreeText: true,
    kind: "catalog",
    catalogChoices,
  };
}

/**
 * The plan, rather than the catalogue search counter, owns the decision. An
 * explicit product/material constraint may need the user when it is
 * unavailable. A free product choice belongs to the agent and must be resolved
 * from the ranked candidates without opening a clarification card.
 */
export function agentOwnsCatalogChoice(input: {
  plan: TaskPlan | null;
  designFreedom: TaskDesignFreedom;
  requestedType: string | null;
  candidates: CatalogCandidate[];
}): boolean {
  if (input.candidates.length === 0) return false;
  const effectiveType = input.requestedType ?? input.candidates.find((candidate) => candidate.type)?.type ?? null;
  const attributes = effectiveType === "material"
    ? new Set(["material", "appearance"])
    : new Set(["product"]);
  const relevant = (input.plan?.decisionAuthorities ?? [])
    .filter((decision) => attributes.has(decision.attribute));
  if (relevant.some((decision) => decision.authority === "explicit")) return false;
  if (relevant.some((decision) => decision.authority === "agent")) return true;
  return input.designFreedom !== "strict";
}

export function selectedCatalogCandidate(prompt: AgentQuestionPrompt, answer: string): CatalogCandidate | null {
  const normalized = normalize(answer);
  return prompt.catalogChoices?.find((choice) => normalize(choice.label) === normalized)?.candidate ?? null;
}

export function catalogCandidatesForAgentDecision(candidates: CatalogCandidate[]): Record<string, unknown>[] {
  return candidates.slice(0, 4).map((candidate) => ({
    catalogItemId: candidate.catalogItemId,
    id: candidate.id,
    stableItemId: candidate.stableItemId,
    revision: candidate.revision,
    name: candidate.name,
    type: candidate.type,
    category: candidate.category,
    description: candidate.description,
    dimensionsMm: candidate.dimensionsMm,
    resize: candidate.resize,
    placementHint: candidate.placementHint,
    availability: candidate.availability,
  }));
}

export function mergeCatalogCandidates(current: CatalogCandidate[], incoming: CatalogCandidate[]): CatalogCandidate[] {
  const merged: CatalogCandidate[] = [];
  const seen = new Set<string>();
  for (const candidate of [...incoming, ...current]) {
    const key = candidate.catalogItemId ?? `${normalize(candidate.name)}:${candidate.type ?? ""}:${candidate.category ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(candidate);
  }
  return merged.slice(0, 12);
}

function candidateLabel(candidate: CatalogCandidate): string {
  const details: string[] = [];
  if (candidate.category) details.push(candidate.category);
  if (candidate.dimensionsMm) {
    const { width, height, depth } = candidate.dimensionsMm;
    details.push(`${formatSize(width)} × ${formatSize(height)} × ${formatSize(depth)} mm`);
  } else if (candidate.description) {
    details.push(clip(candidate.description, 55));
  }
  return clip(`${candidate.name}${details.length ? ` — ${details.join(" · ")}` : ""}`, 120);
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function normalize(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim().toLocaleLowerCase("pt-BR");
}

function formatSize(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(".", ",");
}

function clip(value: string, max: number): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length <= max ? normalized : `${normalized.slice(0, max - 1).trimEnd()}…`;
}
